import { Controller } from 'stimulus';

const COLLAPSED_ATTRIBUTE = 'data-sidenav-collapsed';
// Matches Rails' cookies.permanent, so the rail survives sessions like a preference.
const COOKIE_MAX_AGE = 20 * 365 * 24 * 60 * 60;

export default class extends Controller {
  static targets = ['toggle'];
  static values = {
    collapsible: Boolean,
    collapsed: Boolean,
    persist: Boolean,
    collapsedCookie: String,
    hideLabel: String,
    showLabel: String,
  };

  connect() {
    if (this.collapsibleValue) {
      this.#applyCollapsed(this.collapsedValue);
    }
  }

  // Insurance only: Cache-Control: no-store keeps these pages out of the bfcache today.
  // A restored snapshot predates whatever the user did on the page they came back from,
  // so the rail is re-read rather than trusted.
  restoreOnPageShow(event) {
    if (event.persisted) {
      this.#restoreCollapsed();
    }
  }

  toggleCollapse() {
    const collapsed = !document.body.hasAttribute(COLLAPSED_ATTRIBUTE);

    this.#applyCollapsed(collapsed);
    this.#persist(collapsed);
  }

  #applyCollapsed(collapsed) {
    const label = collapsed ? this.showLabelValue : this.hideLabelValue;

    document.body.toggleAttribute(COLLAPSED_ATTRIBUTE, collapsed);
    this.collapsedValue = collapsed;
    this.toggleTargets.forEach((button) => {
      button.setAttribute('aria-expanded', String(!collapsed));
      button.setAttribute('aria-label', label);
      button.title = label;
    });
  }

  #restoreCollapsed() {
    if (!this.collapsibleValue || !this.persistValue) {
      return;
    }

    this.#applyCollapsed(this.#storedCollapsed());
  }

  #storedCollapsed() {
    return document.cookie
      .split(';')
      .some((entry) => entry.trim().startsWith(`${this.collapsedCookieValue}=`));
  }

  // Presence means collapsed, so expanding deletes the cookie rather than storing "false".
  #persist(collapsed) {
    if (!this.persistValue) {
      return;
    }

    const secure = window.location.protocol === 'https:' ? '; secure' : '';
    const age = collapsed ? COOKIE_MAX_AGE : 0;

    document.cookie = `${this.collapsedCookieValue}=1; path=/; max-age=${age}; samesite=lax${secure}`;
  }
}
